import React, { useState, useEffect, useRef } from 'react';
import Button from '@mui/material-next/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import PropTypes from "prop-types";
import { Autocomplete, Box, Divider, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import Stack from '@mui/material/Stack';
import { useNotifications } from "../../../hooks/notification/notificationHook";
import * as Yup from "yup";
import { Formik, Form } from 'formik'
import { get } from "lodash";
import CustomChip from '../../../widget/CustomChip';
import { ExpandMoreRounded, HighlightOffRounded } from '@material-ui/icons';
import BlurryDialog from '../../../widget/BlurryDialog';
import { campaignJToDto } from '../../../utils/CampaignMapper';

const CampaignDialog = (props) => {
  const { t } = useTranslation();

  const { addSuccessNotification, addErrorNotification } = useNotifications();

  const descriptionElementRef = useRef(null);

  const CampaignSchema = Yup.object().shape({
    campaign: Yup.string()
      .required(t('validation.required'))
      .min(3, t('validation.minimumCharacters'))
      .max(64, t('validation.maximumCharacters')),
    company: Yup.string()
      .required(t('validation.required'))
      .min(3, t('validation.minimumCharacters'))
      .max(64, t('validation.maximumCharacters')),
    country: Yup.object().nullable().required(t('validation.required')),
    managers: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string(),
          label: Yup.string()
        })
      ).test({
        message: t('validation.required'),
        test: arr => arr.length > 0,
      })
  });

  useEffect(() => {
    if (props.open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [props.open]);

  const copyToClipBoard = async copyMe => {
    try {
      await navigator.clipboard.writeText(copyMe);
      addSuccessNotification(t("copiedToClipboard"), t("toast.success"))
    } catch (err) {
      setCopySuccess('Failed to copy!');
    }
  };

  const closeDialog = (resetForm) => {
    resetForm();
    props.handleClose();
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={props.form}
      validationSchema={CampaignSchema}
      onSubmit={(values, { resetForm }) => {
        props.handleSave(campaignJToDto(values), resetForm);
      }}
    >
      {({ handleSubmit, handleChange, values, setFieldValue, resetForm, errors, isValid, dirty }) => (
        <Form>
          <BlurryDialog
            open={props.open}
            onClose={() => closeDialog(resetForm)}
          >
            <DialogTitle
              id="scroll-dialog-title"
              className="color color-medium-purple">
              <b>
                {(props.form.id == "" ? t("campaign.createCampaign") : t("campaign.editCampaign"))}
              </b>
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => closeDialog(resetForm)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent>
              <DialogContentText
                id="scroll-dialog-description"
                ref={descriptionElementRef}
                tabIndex={-1}
              >

                <Stack spacing={3}>
                  <TextField
                    id="dialog-campaign-name"
                    name={'campaign'}
                    value={values.campaign}
                    error={errors.campaign}
                    helperText={errors.campaign}
                    onChange={handleChange}
                    label={t("campaign.campaign")}
                    variant="outlined"
                    style={{ marginBottom: '15px' }}
                    size="small"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px"
                      }
                    }}

                  />
                  <TextField
                    id="dialog-campaign-company"
                    value={values.company}
                    name={'company'}
                    error={errors.company}
                    helperText={errors.company}
                    onChange={handleChange}
                    label={t("campaign.company")}
                    variant="outlined"
                    style={{ marginBottom: '15px' }}
                    size="small"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px"
                      }
                    }}
                  />
                  <Autocomplete
                    id="dialog-campaign-country"
                    options={props.countries}
                    value={get(values, "country")}
                    name={'country'}
                    defaultValue={props.countries[0]}
                    onChange={(event, newValue) => {
                      setFieldValue("country", newValue)
                    }}
                    autoHighlight
                    style={{ marginBottom: '15px' }}
                    getOptionLabel={(option) => option.englishName}
                    renderOption={(props, option) => (
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        <img
                          loading="lazy"
                          width="20"
                          srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                          alt=""
                        />
                        {option.englishName} ({option.code})
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("campaign.country")}
                        size="small"
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px"
                          }
                        }}
                        error={errors.country}
                        helperText={errors.country}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'new-password', // disable autocomplete and autofill
                        }}
                      />
                    )}
                    popupIcon={<ExpandMoreRounded />}
                  />
                  <Autocomplete
                    multiple
                    id="dialog-campaign-managers"
                    value={get(values, "managers")}
                    name={'managers'}
                    onChange={(event, newValue) => {
                      setFieldValue('managers', newValue)
                    }}
                    options={props.managers}
                    getOptionLabel={(option) => option.label}
                    filterSelectedOptions
                    style={{ marginBottom: '15px' }}
                    renderOption={(props, option) => (
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', flexDirection: 'row', fontWeight: '800' }}>
                            {option.name} {option.surname}
                          </div>
                          <div style={{ fontSize: '12px', display: 'flex', flexDirection: 'row' }}>
                            {option.label}
                          </div>
                          <div style={{ fontSize: '12px', display: 'flex', flexDirection: 'row' }}>
                            {option.group}
                          </div>
                        </div>

                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px"
                          }
                        }}
                        label={t("campaign.addManagers")}
                        placeholder={t("campaign.addManagers")}
                      />
                    )}
                    renderTags={(values, getTagProps) =>
                      values.map((value, index) => (
                        <CustomChip
                          id={index} key={index}
                          className="chip bg-dark-gray"
                          size="small"
                          color="#575760"
                          deleteIcon={
                            <HighlightOffRounded />
                          }
                          label={value.label} {...getTagProps({ index })}
                        />
                      ))
                    }
                    popupIcon={<ExpandMoreRounded />}
                  />
                </Stack>

              </DialogContentText>
            </DialogContent>
            <DialogContent>
              <DialogActions>
                {props.form.id != "" && (
                  <Button
                    variant="filled"
                    disableElevation
                    className="bg bg-medium-pink"
                    type="button"
                    size="small"
                    style={{ marginRight: '5px' }}
                    onClick={() => {
                      copyToClipBoard(props.form.id);
                    }}>
                    {t("campaign.copyCapaignId")}
                  </Button>)}
                <Button
                  variant="filled"
                  disableElevation
                  disabled={!isValid || !dirty}
                  className="bg bg-medium-purple"
                  type="submit"
                  size="small"
                  style={{ borderRadius: '10px' }}
                  onClick={handleSubmit}
                >
                  {t("save")}
                </Button>
              </DialogActions>
            </DialogContent>
          </BlurryDialog>
        </Form>
      )}
    </Formik>
  );
}

CampaignDialog.propTypes = {
  form: PropTypes.object,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSave: PropTypes.func,
  managers: PropTypes.array,
  countries: PropTypes.array
};

export default CampaignDialog;